import React, { useState, useEffect } from "react";
import { TextField, Button, MenuItem, Select, FormControl, InputLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import ItemSelectorV2 from "../../../utils/Selector/ItemSelectorV2";
import ModernCalculationSelector from "../../../utils/Selector/ModernCalculationSelector";
import { toast } from "react-hot-toast";
import { PlusCircleIcon, TrashIcon, PlusIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import ProConfirmation from "../../../utils/ProConfirmation";
import { useSettings } from "../../../../contexts/settings-context";
import { fetchTemplates } from "../../../../api/templates";
import { getHourRatesAsync } from "../../../../slices/offer-calculation-hour-rate";
import { useDispatch } from "react-redux";

export default function OfferCalculationItems(props) {
  const { setFieldValue, values, isLoading, name, calculationType, calculationId } = props;
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const dispatch = useDispatch();

  // Settings for main fields visibility
  const [showHours, setShowHours] = useState(values?.show_hours !== undefined ? values.show_hours : true);
  const [showQuantities, setShowQuantities] = useState(values?.show_quantities !== undefined ? values.show_quantities : true);
  const [showProducts, setShowProducts] = useState(values?.show_products !== undefined ? values.show_products : true);
  const [showSetupOnly, setShowSetupOnly] = useState(values?.show_setup_only !== undefined ? values.show_setup_only : false);
  const [showQuantitiesForOfferItemOnly, setShowQuantitiesForOfferItemOnly] = useState(values?.show_quantities_for_offer_item_only !== undefined ? values.show_quantities_for_offer_item_only : false);
  const [includeProductShortDescription, setIncludeProductShortDescription] = useState(values?.include_product_short_description !== undefined ? values.include_product_short_description : false);

  const rows = values[name] || [];

  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [calculationSelectorOpen, setCalculationSelectorOpen] = useState(false);
  const [selectedItemRow, setSelectedItemRow] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedItemForCalculation, setSelectedItemForCalculation] = useState(null);
  const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
  const [editingItemForCustomField, setEditingItemForCustomField] = useState(null);
  const [editingCustomField, setEditingCustomField] = useState(null);
  const [newCustomField, setNewCustomField] = useState({
    key: "",
    label: "",
    type: "text",
    value: "",
  });
  const [selectedItemForCalculationSelect, setSelectedItemForCalculationSelect] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedItemForNote, setSelectedItemForNote] = useState(null);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteShowInOffer, setNoteShowInOffer] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [calculationSettingsDialogOpen, setCalculationSettingsDialogOpen] = useState(false);
  const [selectedItemForSettings, setSelectedItemForSettings] = useState(null);
  const [hourRateSelectorOpen, setHourRateSelectorOpen] = useState(false);
  const [selectedItemForHourRate, setSelectedItemForHourRate] = useState(null);
  const [hourRates, setHourRates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [hourRatesLoaded, setHourRatesLoaded] = useState(false);

  // Fetch templates when dialog opens (when calculationId is set and component is visible)
  useEffect(() => {
    if (calculationId && !templatesLoaded) {
      const loadTemplates = async () => {
        try {
          const response = await fetchTemplates({ per_page: 100 });
          // Handle response structure: response.data.data contains the array
          const templatesData = response?.data?.data || [];
          setTemplates(Array.isArray(templatesData) ? templatesData : []);
          setTemplatesLoaded(true);
        } catch (error) {
          console.error("Error fetching templates:", error);
          setTemplates([]);
          setTemplatesLoaded(true);
        }
      };
      loadTemplates();
    }
  }, [calculationId, templatesLoaded]);

  // Fetch hour rates when dialog opens (when calculationId is set and component is visible)
  useEffect(() => {
    if (calculationId && !hourRatesLoaded) {
      const loadHourRates = async () => {
        try {
          const result = await dispatch(getHourRatesAsync({ active: true, per_page: 100 }));
          if (result.type?.endsWith('/fulfilled')) {
            setHourRates(result.payload?.data || []);
          }
          setHourRatesLoaded(true);
        } catch (error) {
          console.error("Error fetching hour rates:", error);
          setHourRates([]);
          setHourRatesLoaded(true);
        }
      };
      loadHourRates();
    }
  }, [calculationId, hourRatesLoaded, dispatch]);

  const orderItems = (rows) => {
    if (!rows) return [];
    return rows.map((row, i) => ({
      ...row,
      row_order: i,
      draggableId: "id" + (row.id || i),
      // Ensure all required fields are present
      line_type: row.line_type || "manual",
      price_mode: row.price_mode || "custom",
      hours: row.hours || 0,
      hour_rate: row.hour_rate || 0,
      product_id: row.product_id || row.product?.id || null,
      unit_id: row.unit_id || row.unit?.id || null,
      product: row.product || null,
      unit: row.unit || null,
      products: (row.products || []).map(prod => ({
        ...prod,
        divide_by_quantity: prod.divide_by_quantity || false,
        show_image: prod.show_image !== undefined ? prod.show_image : false, // Default to false
        show_url: prod.show_url !== undefined ? prod.show_url : false, // Default to false
      })),
      notes: row.notes || [],
      show_hours: row.show_hours !== undefined ? row.show_hours : false,
      show_image: row.show_image !== undefined ? row.show_image : true,
      show_url: row.show_url !== undefined ? row.show_url : true,
      required_fields: row.required_fields || {},
      editable_quantity: row.editable_quantity !== undefined ? row.editable_quantity : true,
      divide_by_quantity: row.divide_by_quantity || false,
      nested_calculation: row.nested_calculation ? {
        ...row.nested_calculation,
        divide_by_quantity: row.nested_calculation.divide_by_quantity || false,
      } : null,
      custom_fields: row.custom_fields || {},
    }));
  };

  const defaultRows = [
    {
      id: null,
      row_order: 0,
      description: "",
      purchase_price: 0,
      sales_price: 0,
      quantity: 0,
      discount: 0,
      surcharge: 0,
      item_type: calculationType || "product",
      line_type: "manual", // "manual", "product", "calculation", "hours"
      price_mode: "custom", // "automatic" or "custom"
      hours: 0,
      hour_rate: 0,
      cost_hour_rate: 0, // Cost hour rate for worst case calculation
      divide_by_quantity: false, // Divide calculation by quantity
      custom_fields: {},
      notes: [], // Array of notes/templates for this line
      show_hours: false, // Whether to show hours calculation section
      required_fields: {}, // Object with field names as keys and boolean values for required status
      editable_quantity: true, // Whether quantity can be edited when calculation is used in offer
    },
  ];

  const [items, setItems] = useState(() => {
    const initialRows = values[name];
    // Only use defaultRows if initialRows is undefined/null (not loaded yet)
    // If initialRows is an empty array, it means calculation has 0 items, so use empty array
    let rowsToUse;
    if (initialRows !== undefined && initialRows !== null) {
      rowsToUse = initialRows; // Use the actual rows (even if empty)
    } else {
      rowsToUse = defaultRows; // Use default rows only if not loaded yet
    }
    const initialItems = orderItems(rowsToUse);
    console.log('[OfferCalculationItems] Initial state:', {
      initialRows: initialRows,
      initialRowsLength: initialRows?.length,
      rowsToUse: rowsToUse,
      rowsToUseLength: rowsToUse?.length,
      initialItems: initialItems,
      initialItemsLength: initialItems?.length,
      name: name,
      valuesName: values[name],
      isUndefined: initialRows === undefined,
      isNull: initialRows === null
    });
    return initialItems;
  });

  // Use refs to track previous values and prevent unnecessary updates
  const prevRowsRef = React.useRef(rows);
  const isInternalUpdateRef = React.useRef(false);
  const isInitialMountRef = React.useRef(true);

  // Update items state when rows change from external source (e.g., when calculation is loaded)
  useEffect(() => {
    console.log('[OfferCalculationItems] useEffect rows changed:', {
      isInitialMount: isInitialMountRef.current,
      isInternalUpdate: isInternalUpdateRef.current,
      rows: rows,
      rowsLength: rows?.length,
      prevRows: prevRowsRef.current,
      prevRowsLength: prevRowsRef.current?.length,
      currentItems: items,
      currentItemsLength: items?.length
    });

    // On initial mount, always sync items with rows
    if (isInitialMountRef.current) {
      console.log('[OfferCalculationItems] Initial mount - syncing items with rows');
      isInitialMountRef.current = false;
      // Only sync if rows is defined (not undefined/null)
      // If rows is an empty array, it means calculation has 0 items, so keep empty array
      if (rows !== undefined && rows !== null) {
        const orderedItems = orderItems(rows);
        console.log('[OfferCalculationItems] Initial mount - setting items:', orderedItems);
        setItems(orderedItems);
        prevRowsRef.current = rows;
      } else {
        // rows is undefined/null - calculation not loaded yet, keep default items
        console.log('[OfferCalculationItems] Initial mount - rows is undefined/null, keeping default items');
        prevRowsRef.current = rows;
      }
      return;
    }

    // Skip if this is an internal update (from setFieldValue)
    if (isInternalUpdateRef.current) {
      console.log('[OfferCalculationItems] Skipping update - internal update flag set');
      isInternalUpdateRef.current = false;
      return;
    }

    // Compare rows by checking IDs and length
    const prevIds = prevRowsRef.current?.map(r => r?.id) || [];
    const currentIds = rows?.map(r => r?.id) || [];
    const idsChanged = JSON.stringify(prevIds) !== JSON.stringify(currentIds);
    const lengthChanged = (prevRowsRef.current?.length || 0) !== (rows?.length || 0);
    
    // Also check if rows has data but items doesn't match (safety check)
    const currentItemsIds = items?.map(i => i?.id) || [];
    const itemsDontMatchRows = rows && rows.length > 0 && (
      items.length === 0 || 
      JSON.stringify(currentItemsIds.sort()) !== JSON.stringify(currentIds.sort())
    );
    
    console.log('[OfferCalculationItems] Comparison results:', {
      prevIds: prevIds,
      currentIds: currentIds,
      currentItemsIds: currentItemsIds,
      idsChanged: idsChanged,
      lengthChanged: lengthChanged,
      itemsDontMatchRows: itemsDontMatchRows,
      shouldUpdate: idsChanged || lengthChanged || itemsDontMatchRows
    });
    
    if (idsChanged || lengthChanged || itemsDontMatchRows) {
      console.log('[OfferCalculationItems] Updating items from rows');
      // Only update if rows is defined (not undefined/null)
      // If rows is an empty array, it means calculation has 0 items, so set to empty array
      if (rows !== undefined && rows !== null) {
        const orderedItems = orderItems(rows);
        console.log('[OfferCalculationItems] Setting items to:', orderedItems);
        setItems(orderedItems);
        prevRowsRef.current = rows;
      }
      // If rows is undefined/null, don't update (calculation not loaded yet)
    } else {
      console.log('[OfferCalculationItems] No update needed - rows and items match');
    }
  }, [rows]);

  // Update Formik when items change (but mark as internal update to prevent loop)
  useEffect(() => {
    console.log('[OfferCalculationItems] useEffect items changed:', {
      isInitialMount: isInitialMountRef.current,
      items: items,
      itemsLength: items?.length,
      name: name,
      setFieldValue: !!setFieldValue
    });
    
    if (setFieldValue && !isInitialMountRef.current) {
      console.log('[OfferCalculationItems] Updating Formik with items:', items);
      isInternalUpdateRef.current = true;
      setFieldValue(name, items);
    }
  }, [items, name, setFieldValue]);

  // Sync settings from values when calculation is loaded
  useEffect(() => {
    if (values?.show_setup_only !== undefined) {
      setShowSetupOnly(values.show_setup_only);
    }
    if (values?.show_quantities_for_offer_item_only !== undefined) {
      setShowQuantitiesForOfferItemOnly(values.show_quantities_for_offer_item_only);
    }
    if (values?.include_product_short_description !== undefined) {
      setIncludeProductShortDescription(values.include_product_short_description);
    }
  }, [values?.show_setup_only, values?.show_quantities_for_offer_item_only, values?.include_product_short_description]);

  // Update parent values when settings change (removed duplicate)
  useEffect(() => {
    if (setFieldValue) {
      setFieldValue("show_hours", showHours);
      setFieldValue("show_quantities", showQuantities);
      setFieldValue("show_products", showProducts);
      setFieldValue("show_setup_only", showSetupOnly);
      setFieldValue("show_quantities_for_offer_item_only", showQuantitiesForOfferItemOnly);
      setFieldValue("include_product_short_description", includeProductShortDescription);
    }
  }, [showHours, showQuantities, showProducts, showSetupOnly, showQuantitiesForOfferItemOnly, includeProductShortDescription, setFieldValue]);

  const addItemRow = (lineType = "manual") => {
    const newRow = {
      description: "",
      purchase_price: 0,
      sales_price: 0,
      quantity: 0,
      discount: 0,
      surcharge: 0,
      row_order: items.length,
      item_type: calculationType || "product",
      line_type: lineType, // "manual", "product", "calculation", "hours"
      price_mode: lineType === "product" ? "custom" : "custom", // Product lines always use custom (manual) pricing
      hours: 0,
      hour_rate: 0,
      cost_hour_rate: 0, // Cost hour rate for worst case calculation
      nested_calculation_id: null,
      nested_calculation: null,
      products: [], // Array of products for this line
      notes: [], // Array of notes/templates for this line
      show_hours: lineType === "hours", // Automatically show hours section for hours lines
      show_image: lineType === "product", // Show product image in PDF
      show_url: lineType === "product", // Show product URL in PDF
      required_fields: {}, // Object with field names as keys and boolean values for required status
      editable_quantity: true, // Whether quantity can be edited when calculation is used in offer
      draggableId: "id" + (items.length + 1),
    };
    setItems((prevState) => [...prevState, newRow]);
  };

  // Helper function to format number with comma as decimal separator
  const formatNumber = (value) => {
    if (value === "" || value === null || value === undefined) return "0,00";
    const numValue = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
    if (isNaN(numValue)) return "0,00";
    return numValue.toFixed(2).replace(".", ",");
  };

  // Helper function to parse formatted number back to number
  const parseFormattedNumber = (value) => {
    if (!value || value === "") return 0;
    const cleaned = String(value).replace(/\s/g, "").replace(",", ".");
    return parseFloat(cleaned) || 0;
  };

  const onItemRowChange = (e, data) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    let prevItems = [...items];
    let item = {
      ...prevItems[data.row_order],
      [e.target.name]: e.target.value,
    };
    prevItems[data.row_order] = item;
    setItems(prevItems);
  };

  const onCustomFieldChange = (fieldKey, value, item) => {
    let prevItems = [...items];
    let customFields = { ...(prevItems[item.row_order].custom_fields || {}) };
    customFields[fieldKey] = value;
    
    let updatedItem = {
      ...prevItems[item.row_order],
      custom_fields: customFields,
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const openCustomFieldDialog = (item, fieldKey = null) => {
    setEditingItemForCustomField(item);
    if (fieldKey) {
      // Editing existing field
      const customFields = item.custom_fields || {};
      setEditingCustomField(fieldKey);
      setNewCustomField({
        key: fieldKey,
        label: fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1).replace(/_/g, " "),
        type: typeof customFields[fieldKey] === "number" ? "number" : "text",
        value: customFields[fieldKey] || "",
      });
    } else {
      // Adding new field
      setEditingCustomField(null);
      setNewCustomField({
        key: "",
        label: "",
        type: "number",
        value: "",
      });
    }
    setCustomFieldDialogOpen(true);
  };

  const handleSaveCustomField = () => {
    if (!editingItemForCustomField || !newCustomField.key || !newCustomField.label) {
      toast.error("Fyll ut alle felt");
      return;
    }

    let prevItems = [...items];
    let item = prevItems.find((i) => i.row_order === editingItemForCustomField.row_order);
    if (!item) return;

    let customFields = { ...(item.custom_fields || {}) };
    
    // If editing, remove old key first if it changed
    if (editingCustomField && editingCustomField !== newCustomField.key) {
      delete customFields[editingCustomField];
    }

    // Convert value based on type
    let value = newCustomField.value;
    if (newCustomField.type === "number") {
      value = parseFloat(value) || 0;
    }

    customFields[newCustomField.key] = value;

    let updatedItem = {
      ...item,
      custom_fields: customFields,
    };
    
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
    setCustomFieldDialogOpen(false);
    setEditingCustomField(null);
    setEditingItemForCustomField(null);
  };

  const handleDeleteCustomField = (item, fieldKey) => {
    let prevItems = [...items];
    let customFields = { ...(prevItems[item.row_order].custom_fields || {}) };
    delete customFields[fieldKey];
    
    let updatedItem = {
      ...prevItems[item.row_order],
      custom_fields: customFields,
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const renderCustomFields = (item) => {
    const customFields = item.custom_fields || {};
    const fieldKeys = Object.keys(customFields);

    if (fieldKeys.length === 0) {
      return (
        <div className="col-span-12">
          <div className="text-sm text-gray-500 mb-2">Ingen custom fields</div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openCustomFieldDialog(item)}
            startIcon={<PlusIcon className="h-4 w-4" />}
          >
            Legg til felt
          </Button>
        </div>
      );
    }

    return (
      <div className="col-span-12">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium text-gray-700">Custom Fields:</div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openCustomFieldDialog(item)}
            startIcon={<PlusIcon className="h-4 w-4" />}
          >
            Legg til felt
          </Button>
        </div>
        <div className="grid grid-cols-12 gap-2">
          {fieldKeys.map((key) => (
            <div key={key} className="col-span-12 sm:col-span-4 flex items-center gap-2">
              <TextField
                label={key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")}
                variant="standard"
                type={typeof customFields[key] === "number" ? "number" : "text"}
                value={customFields[key] || ""}
                onChange={(e) => onCustomFieldChange(key, e.target.value, item)}
                fullWidth
                size="small"
              />
              <IconButton
                size="small"
                onClick={() => openCustomFieldDialog(item, key)}
                className="text-blue-500"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteCustomField(item, key)}
                className="text-red-500"
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Calculate automatic sales price based on nested calculation and products
  const calculateAutomaticSalesPrice = (item) => {
    let total = 0;

    // Add nested calculation total if exists
    if (item.nested_calculation?.total_amount) {
      let nestedAmount = parseFloat(item.nested_calculation.total_amount || 0);
      // If divide_by_quantity is true on the nested calculation itself, divide by item quantity
      if (item.nested_calculation.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedAmount = nestedAmount / parseFloat(item.quantity);
      }
      // If divide_by_quantity is true on the item level, also divide by quantity
      else if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedAmount = nestedAmount / parseFloat(item.quantity);
      }
      total += nestedAmount;
    }

    // Add products totals if exists
    if (item.products && Array.isArray(item.products)) {
      item.products.forEach((prod) => {
        const prodQuantity = parseFloat(prod.quantity || 1);
        let prodSalesPrice = parseFloat(prod.sales_price || 0);
        const prodDiscount = parseFloat(prod.discount || 0);
        const prodSurcharge = parseFloat(prod.surcharge || 0);
        
        // Only divide products if product-level divide_by_quantity is set
        // Item-level divide_by_quantity only affects hours and nested calculations, not products
        if (prod.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
          prodSalesPrice = prodSalesPrice / parseFloat(item.quantity);
        }
        
        if (prodSalesPrice > 0 && prodQuantity > 0) {
          let prodTotal = prodSalesPrice * prodQuantity;
          if (prodSurcharge > 0) prodTotal += (prodTotal * prodSurcharge) / 100;
          if (prodDiscount > 0) prodTotal -= (prodTotal * prodDiscount) / 100;
          total += prodTotal;
        }
      });
    }

    // Add hours calculation if exists
    let hours = parseFormattedNumber(item.hours || 0);
    const hourRate = parseFormattedNumber(item.hour_rate || 0);
    const quantity = parseFormattedNumber(item.quantity || 1);
    // If divide_by_quantity is true, divide hours by quantity
    if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
      hours = hours / parseFloat(item.quantity);
    }
    // Multiply hours by quantity for total calculation (hours per unit * quantity = total hours)
    if (hours > 0 && hourRate > 0) {
      total += hours * hourRate * quantity;
    }

    return total;
  };

  // Calculate automatic purchase price based on nested calculation, products, and hours cost
  const calculateAutomaticPurchasePrice = (item) => {
    let total = 0;

    // Add hours cost calculation if exists
    // Calculate cost as: cost_hour_rate * hours * quantity (same logic as sales price)
    let hours = parseFormattedNumber(item.hours || 0);
    const costHourRate = parseFormattedNumber(item.cost_hour_rate || 0);
    const quantity = parseFormattedNumber(item.quantity || 1);
    
    // If divide_by_quantity is true, divide hours by quantity
    if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
      hours = hours / parseFloat(item.quantity);
    }
    
    if (hours > 0 && costHourRate > 0) {
      // Calculate cost hours total: hours * cost_hour_rate * quantity
      // Same logic as sales price: hour_rate * hours * quantity
      total += hours * costHourRate * quantity;
    }

    // Add nested calculation purchase amount if exists
    if (item.nested_calculation?.purchase_amount !== undefined) {
      let nestedCost = parseFloat(item.nested_calculation.purchase_amount || 0);
      // If divide_by_quantity is true on the nested calculation itself, divide by item quantity
      if (item.nested_calculation.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedCost = nestedCost / parseFloat(item.quantity);
      }
      // If divide_by_quantity is true on the item level, also divide by quantity
      else if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedCost = nestedCost / parseFloat(item.quantity);
      }
      total += nestedCost;
    }

    // Add products purchase prices if exists
    if (item.products && Array.isArray(item.products)) {
      item.products.forEach((prod) => {
        const prodQuantity = parseFloat(prod.quantity || 1);
        let prodPurchasePrice = parseFloat(prod.purchase_price || 0);
        
        // Only divide products if product-level divide_by_quantity is set
        // Item-level divide_by_quantity only affects hours and nested calculations, not products
        if (prod.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
          prodPurchasePrice = prodPurchasePrice / parseFloat(item.quantity);
        }
        
        if (prodPurchasePrice > 0 && prodQuantity > 0) {
          total += prodPurchasePrice * prodQuantity;
        }
      });
    }

    return total;
  };

  const calculateRowTotalPrice = (item) => {
    let total = 0;
    const quantity = parseFormattedNumber(item.quantity || 0);
    const surcharge = parseFormattedNumber(item.surcharge || 0);
    const discount = parseFormattedNumber(item.discount || 0);

    // Use automatic or custom sales price
    let salesPrice = 0;
    if (item.price_mode === "automatic") {
      salesPrice = calculateAutomaticSalesPrice(item);
    } else {
      salesPrice = parseFormattedNumber(item.sales_price || 0);
    }

    if (salesPrice > 0 && quantity > 0) {
      total = salesPrice * quantity;
      if (surcharge > 0) total += (total * surcharge) / 100;
      if (discount > 0) total -= (total * discount) / 100;
    }

    return total;
  };

  const calculateTotalPrice = (items) => {
    let total = 0;
    if (items.length > 0) {
      items.forEach((item) => {
        total += calculateRowTotalPrice(item);
      });
    }
    return total;
  };

  const handleRowProductSelect = (row) => {
    setSelectedItemRow(row);
    setProductSelectorOpen(true);
  };

  const handleProductSelect = (data) => {
    setProductSelectorOpen(false);
    if (selectedItemRow && data && data.length > 0) {
      let prevItems = [...items];
      let item = prevItems[selectedItemRow.row_order];
      
      // If adding to products array (multiple products)
      if (selectedItemForCalculation) {
        const products = item.products || [];
        const newProducts = [...products];
        
        data.forEach((prod) => {
          // Check if product already exists
          const exists = newProducts.find((p) => p.id === prod.id);
          if (!exists) {
            newProducts.push({
              id: prod.id,
              displayTitle: prod.displayTitle,
              sales_price: prod.sales_price || 0,
              purchase_price: prod.purchase_price || 0,
              quantity: 1,
              discount: 0,
              surcharge: 0,
              divide_by_quantity: false,
              show_image: false, // Default to false for products in "Kalkyler og produkter"
              show_url: false, // Default to false for products in "Kalkyler og produkter"
              unit: prod.unit,
            });
          }
        });
        
        item.products = newProducts;
      } else {
        // Single product selection (main product)
        item.product = data[0];
        item.product_id = data[0].id;
        item.unit_id = data[0].unit?.id;
        item.unit = data[0].unit;
        item.sales_price = data[0].sales_price || 0;
        item.purchase_price = data[0].purchase_price || 0;
        item.quantity = "1.0";
      }
      
      prevItems[selectedItemRow.row_order] = item;
      setItems(prevItems);
      
      // Update Formik form to ensure product_id is saved
      const mappedItems = prevItems.map(item => ({
        ...item,
        item_type: item.item_type || item.type,
        product_id: item.product_id || item.product?.id || null,
        unit_id: item.unit_id || item.unit?.id || null,
      }));
      setFieldValue(name, mappedItems);
      
      setSelectedItemForCalculation(null);
    }
  };

  const handleProductFieldChange = (item, productId, field, value) => {
    let prevItems = [...items];
    let updatedItem = {
      ...prevItems[item.row_order],
      products: (item.products || []).map((prod) => {
        if (prod.id === productId) {
          return {
            ...prod,
            [field]: value,
          };
        }
        return prod;
      }),
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
    // Update Formik form
    const mappedItems = prevItems.map(item => ({
      ...item,
      item_type: item.item_type || item.type,
      product_id: item.product_id || item.product?.id || null,
      unit_id: item.unit_id || item.unit?.id || null,
    }));
    setFieldValue(name, mappedItems);
  };

  const handleRemoveNestedCalculation = (item, calcId) => {
    let prevItems = [...items];
    let updatedItem = {
      ...prevItems[item.row_order],
      nested_calculation: null,
      nested_calculation_id: null,
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const handleRemoveProduct = (item, productId) => {
    let prevItems = [...items];
    let updatedItem = {
      ...prevItems[item.row_order],
      products: (item.products || []).filter((p) => p.id !== productId),
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const deleteRow = (rowOrder) => {
    setSelectedItemRow(rowOrder);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    if (selectedItemRow !== null) {
      const newArray = items.filter(
        (item) => item.row_order !== selectedItemRow
      );
      setItems(orderItems(newArray));
    }
  };

  const renderItemRow = (item, index) => {
    const isProductType = calculationType === "product" || item.item_type === "product";
    
    return (
      <div
        key={item.draggableId || index}
        className="bg-white shadow-md rounded-xl mb-4 p-4"
      >
        {/* Standard Fields Section */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-700">
              {item?.line_type === "manual" && "Manuell linje"}
            {item?.line_type === "calculation" && "Beregningslinje"}
            {item?.line_type === "hours" && "Timelinje"}
            {item?.line_type === "product" && "Produktlinje"}
            {!item?.line_type && "Standard Felter"}
          </div>
          <div className="flex items-center gap-2">
            {item?.line_type && (
              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {item.line_type === "manual" && "Manuell"}
                {item.line_type === "calculation" && "Beregning"}
                {item.line_type === "hours" && "Timer"}
                {item.line_type === "product" && "Produkt"}
              </div>
            )}
            {item?.line_type === "calculation" && (
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedItemForSettings(item);
                  setCalculationSettingsDialogOpen(true);
                }}
                title={t("Innstillinger for beregningslinje")}
                className="text-gray-600 hover:text-indigo-600"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </IconButton>
            )}
          </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            {/* Product Selection - Show for product lines and other non-manual/calculation lines */}
            {item?.line_type === "product" && (
              <div className="col-span-12 sm:col-span-3">
                <div
                  className="cursor-pointer p-2 border-2 border-dashed border-gray-300 rounded hover:border-indigo-500"
                  onClick={() => handleRowProductSelect(item)}
                >
                  {item?.product?.id ? (
                    <div className="font-bold text-indigo-600">
                      {item?.product?.displayTitle}
                    </div>
                  ) : (
                    <div className="text-gray-400">Velg produkt</div>
                  )}
                </div>
              </div>
            )}
            {/* Product Selection - Hide for manual and calculation lines (products are added in "Kalkyler og produkter" section) */}
            {item?.line_type !== "manual" && item?.line_type !== "calculation" && item?.line_type !== "product" && (
              <div className="col-span-12 sm:col-span-3">
                <div
                  className="cursor-pointer p-2 border-2 border-dashed border-gray-300 rounded hover:border-indigo-500"
                  onClick={() => handleRowProductSelect(item)}
                >
                  {item?.product?.id ? (
                    <div className="font-bold text-indigo-600">
                      {item?.product?.displayTitle}
                    </div>
                  ) : (
                    <div className="text-gray-400">Velg produkt (valgfritt)</div>
                  )}
                </div>
              </div>
            )}

            {/* Description - Available for all types */}
            <div className="col-span-12 sm:col-span-4">
              <TextField
                label="Beskrivelse"
                variant="standard"
                fullWidth
                name="description"
                value={item?.description || ""}
                onChange={(e) => onItemRowChange(e, item)}
                multiline
                rows={2}
              />
            </div>

            {/* Quantity - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <div className="flex items-center gap-2">
                <TextField
                  label="Antall/Mengde"
                  variant="standard"
                  type="text"
                  name="quantity"
                  value={item?.quantity || 0}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const cleanedValue = rawValue.replace(",", ".");
                    // Update directly instead of using onItemRowChange to avoid preventDefault issues
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      quantity: cleanedValue,
                    };
                    setItems(prevItems);
                  }}
                  onBlur={(e) => {
                    const formatted = formatNumber(e.target.value);
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      quantity: formatted,
                    };
                    
                    // Recalculate hours if auto-calculate is enabled when quantity changes
                    if (prevItems[item.row_order]?.calculate_hours_from_sales && prevItems[item.row_order]?.line_type === "hours") {
                      const salesPrice = parseFormattedNumber(prevItems[item.row_order].sales_price || 0);
                      const quantity = parseFormattedNumber(formatted || 1);
                      const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                      const hourRate = parseFormattedNumber(prevItems[item.row_order].hour_rate || 0);
                      if (hourRate > 0 && totalSalesPrice > 0) {
                        const calculatedHours = totalSalesPrice / hourRate;
                        prevItems[item.row_order].hours = calculatedHours.toFixed(2);
                        prevItems[item.row_order].calculated_hours_from_sales = calculatedHours;
                      }
                    }
                    
                    // Recalculate worst case hours when quantity changes
                    if (prevItems[item.row_order]?.line_type === "hours" && prevItems[item.row_order]?.cost_hour_rate) {
                      const salesPrice = parseFormattedNumber(prevItems[item.row_order].sales_price || 0);
                      const quantity = parseFormattedNumber(formatted || 1);
                      const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                      const costHourRate = parseFormattedNumber(prevItems[item.row_order].cost_hour_rate || 0);
                      if (costHourRate > 0 && totalSalesPrice > 0) {
                        const calculatedHours = totalSalesPrice / costHourRate;
                        prevItems[item.row_order].calculated_hours_from_cost = calculatedHours;
                      }
                    }
                    
                    setItems(prevItems);
                  }}
                  fullWidth
                  required={item?.required_fields?.quantity}
                  disabled={!item?.editable_quantity}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    let prevItems = [...items];
                    const currentRequired = prevItems[item.row_order].required_fields || {};
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      required_fields: {
                        ...currentRequired,
                        quantity: !currentRequired.quantity,
                      },
                    };
                    setItems(prevItems);
                  }}
                  title={item?.required_fields?.quantity ? "Ikke påkrevd" : "Gjør påkrevd"}
                >
                  {item?.required_fields?.quantity ? (
                    <span className="text-red-500 text-xs">*</span>
                  ) : (
                    <span className="text-gray-400 text-xs">*</span>
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      editable_quantity: !prevItems[item.row_order].editable_quantity,
                    };
                    setItems(prevItems);
                  }}
                  title={item?.editable_quantity ? "Ikke redigerbar i tilbud" : "Redigerbar i tilbud"}
                >
                  <PencilSquareIcon className={`h-4 w-4 ${item?.editable_quantity ? "text-blue-500" : "text-gray-400"}`} />
                </IconButton>
              </div>
            </div>

            {/* Unit - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label="Enhet"
                variant="standard"
                disabled
                value={item?.unit?.title || "Stk"}
                fullWidth
              />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4 mt-4">
            {/* Price Mode Selection - Hide for manual lines and product lines */}
            {item?.line_type !== "manual" && item?.line_type !== "product" && (
              <div className="col-span-12 sm:col-span-3">
                <FormControl variant="standard" fullWidth>
                  <InputLabel>Prisberegning</InputLabel>
                  <Select
                    name="price_mode"
                    value={item?.price_mode || "custom"}
                    onChange={(e) => onItemRowChange(e, item)}
                    label="Prisberegning"
                  >
                    <MenuItem value="automatic">Automatisk (fra kalkyle/produkter/timer)</MenuItem>
                    <MenuItem value="custom">Manuell</MenuItem>
                  </Select>
                </FormControl>
              </div>
            )}

            {/* Prices - Show based on mode */}
            {item?.line_type === "manual" || item?.line_type === "product" ? (
              // Manual lines - always editable, no price mode selection
              <>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label="Salgspris"
                    variant="standard"
                    type="text"
                    name="sales_price"
                    value={item?.sales_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        sales_price: cleanedValue,
                      };
                      setItems(prevItems);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        sales_price: formatted,
                      };
                      setItems(prevItems);
                    }}
                    fullWidth
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label="Kostpris"
                    variant="standard"
                    type="text"
                    name="purchase_price"
                    value={item?.purchase_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        purchase_price: cleanedValue,
                      };
                      setItems(prevItems);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        purchase_price: formatted,
                      };
                      setItems(prevItems);
                    }}
                    fullWidth
                  />
                </div>
              </>
            ) : item?.price_mode === "automatic" ? (
              <>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label="Salgspris (auto)"
                    variant="standard"
                    type="number"
                    disabled
                    value={calculateAutomaticSalesPrice(item).toFixed(2)}
                    fullWidth
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label="Kostpris (auto)"
                    variant="standard"
                    type="number"
                    disabled
                    value={calculateAutomaticPurchasePrice(item).toFixed(2)}
                    fullWidth
                  />
                </div>
              </>
            ) : (
              <>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label="Salgspris"
                    variant="standard"
                    type="text"
                    name="sales_price"
                    value={item?.sales_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Allow typing with comma or dot
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      
                      // Auto-calculate hours if enabled (for hours lines with custom price_mode)
                      if (item?.calculate_hours_from_sales && item?.line_type === "hours") {
                        const salesPrice = parseFormattedNumber(cleanedValue);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: cleanedValue,
                            hours: calculatedHours.toFixed(2),
                            calculated_hours_from_sales: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: cleanedValue,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines when sales_price changes: (salgspris * mengde) / kostpris timesats
                        if (item?.line_type === "hours" && item?.cost_hour_rate) {
                          const salesPrice = parseFormattedNumber(cleanedValue);
                          const quantity = parseFormattedNumber(item?.quantity || 1);
                          const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                          const costHourRate = parseFormattedNumber(item?.cost_hour_rate || 0);
                          if (costHourRate > 0 && totalSalesPrice > 0) {
                            const calculatedHours = totalSalesPrice / costHourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: cleanedValue,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: cleanedValue,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: cleanedValue,
                          };
                        }
                      }
                      setItems(prevItems);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      
                      // Recalculate hours if auto-calculate is enabled (for hours lines)
                      if (item?.calculate_hours_from_sales && item?.line_type === "hours") {
                        const salesPrice = parseFormattedNumber(formatted);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: formatted,
                            hours: calculatedHours.toFixed(2),
                            calculated_hours_from_sales: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: formatted,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines when sales_price changes: (salgspris * mengde) / kostpris timesats
                        if (item?.line_type === "hours" && item?.cost_hour_rate) {
                          const salesPrice = parseFormattedNumber(formatted);
                          const quantity = parseFormattedNumber(item?.quantity || 1);
                          const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                          const costHourRate = parseFormattedNumber(item?.cost_hour_rate || 0);
                          if (costHourRate > 0 && totalSalesPrice > 0) {
                            const calculatedHours = totalSalesPrice / costHourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: formatted,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: formatted,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: formatted,
                          };
                        }
                      }
                      setItems(prevItems);
                    }}
                    disabled={item?.calculate_hours_from_cost && item?.line_type === "hours"}
                    fullWidth
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label="Kostpris"
                    variant="standard"
                    type="text"
                    name="purchase_price"
                    value={item?.purchase_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Allow typing with comma or dot
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      
                      // Auto-calculate hours if enabled (for hours lines with custom price_mode)
                      if (item?.calculate_hours_from_cost && item?.line_type === "hours") {
                        const costPrice = parseFormattedNumber(cleanedValue);
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && costPrice > 0) {
                          const calculatedHours = costPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: cleanedValue,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: cleanedValue,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines (even if checkbox not checked)
                        if (item?.line_type === "hours" && item?.hour_rate) {
                          const costPrice = parseFormattedNumber(cleanedValue);
                          const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                          if (hourRate > 0 && costPrice > 0) {
                            const calculatedHours = costPrice / hourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: cleanedValue,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: cleanedValue,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: cleanedValue,
                          };
                        }
                      }
                      setItems(prevItems);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      
                      // Recalculate hours if auto-calculate is enabled (for hours lines)
                      if (item?.calculate_hours_from_cost && item?.line_type === "hours") {
                        const costPrice = parseFormattedNumber(formatted);
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && costPrice > 0) {
                          const calculatedHours = costPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: formatted,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: formatted,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines (even if checkbox not checked)
                        if (item?.line_type === "hours" && item?.hour_rate) {
                          const costPrice = parseFormattedNumber(formatted);
                          const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                          if (hourRate > 0 && costPrice > 0) {
                            const calculatedHours = costPrice / hourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: formatted,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: formatted,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: formatted,
                          };
                        }
                      }
                      setItems(prevItems);
                    }}
                    disabled={item?.calculate_hours_from_sales && item?.line_type === "hours"}
                    fullWidth
                  />
                </div>
              </>
            )}

            {/* Discount - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label="Rabatt %"
                variant="standard"
                type="text"
                name="discount"
                value={item?.discount || 0}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const cleanedValue = rawValue.replace(",", ".");
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    discount: cleanedValue,
                  };
                  setItems(prevItems);
                }}
                onBlur={(e) => {
                  const formatted = formatNumber(e.target.value);
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    discount: formatted,
                  };
                  setItems(prevItems);
                }}
                fullWidth
              />
            </div>

            {/* Surcharge - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label="Påslag %"
                variant="standard"
                type="text"
                name="surcharge"
                value={item?.surcharge || 0}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const cleanedValue = rawValue.replace(",", ".");
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    surcharge: cleanedValue,
                  };
                  setItems(prevItems);
                }}
                onBlur={(e) => {
                  const formatted = formatNumber(e.target.value);
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    surcharge: formatted,
                  };
                  setItems(prevItems);
                }}
                fullWidth
              />
            </div>

            {/* Total - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <div className="text-right">
                <div className="text-sm text-gray-500">Total:</div>
                <div className="font-bold text-lg">
                  {priceFormatter.format(calculateRowTotalPrice(item))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hours Calculation Section - Show only if explicitly enabled, but NOT for manual lines or product lines */}
        {item?.show_hours && item?.line_type !== "manual" && item?.line_type !== "product" && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-gray-700">Timeberegning:</div>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    show_hours: false,
                  };
                  setItems(prevItems);
                }}
              >
                Skjul
              </Button>
            </div>
            
            {/* Auto-calculate hours from price options - For hours lines when price_mode is custom */}
            {(item?.line_type === "hours" && item?.price_mode === "custom") && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-gray-700 mb-2">Beregn timeantall automatisk:</div>
                <div className="flex items-center gap-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item?.calculate_hours_from_sales || false}
                        onChange={(e) => {
                          let prevItems = [...items];
                          const checked = e.target.checked;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            calculate_hours_from_sales: checked,
                            calculate_hours_from_cost: checked ? false : prevItems[item.row_order].calculate_hours_from_cost,
                          };
                          // Auto-calculate if enabled
                          if (checked) {
                            const salesPrice = parseFormattedNumber(prevItems[item.row_order].sales_price || 0);
                            const quantity = parseFormattedNumber(prevItems[item.row_order].quantity || 1);
                            const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                            const hourRate = parseFormattedNumber(prevItems[item.row_order].hour_rate || 0);
                            if (hourRate > 0 && totalSalesPrice > 0) {
                              const calculatedHours = totalSalesPrice / hourRate;
                              prevItems[item.row_order].hours = calculatedHours.toFixed(2);
                              prevItems[item.row_order].calculated_hours_from_sales = calculatedHours;
                            }
                          } else {
                            // Clear calculated hours when unchecked
                            prevItems[item.row_order].calculated_hours_from_sales = null;
                          }
                          setItems(prevItems);
                        }}
                      />
                    }
                    label="Ut fra salgspris"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item?.calculate_hours_from_cost || false}
                        onChange={(e) => {
                          let prevItems = [...items];
                          const checked = e.target.checked;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            calculate_hours_from_cost: checked,
                            calculate_hours_from_sales: checked ? false : prevItems[item.row_order].calculate_hours_from_sales,
                          };
                          // Auto-calculate if enabled
                          if (checked) {
                            const costPrice = parseFormattedNumber(prevItems[item.row_order].purchase_price || 0);
                            const hourRate = parseFormattedNumber(prevItems[item.row_order].hour_rate || 0);
                            if (hourRate > 0 && costPrice > 0) {
                              const calculatedHours = costPrice / hourRate;
                              prevItems[item.row_order].calculated_hours_from_cost = calculatedHours;
                            }
                          } else {
                            // Clear calculated hours when unchecked
                            prevItems[item.row_order].calculated_hours_from_cost = null;
                          }
                          setItems(prevItems);
                        }}
                      />
                    }
                    label="Ut fra kostpris"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6 sm:col-span-3">
                <TextField
                  label="Antall timer"
                  variant="standard"
                  type="text"
                  name="hours"
                  value={item?.hours || 0}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const cleanedValue = rawValue.replace(",", ".");
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      hours: cleanedValue,
                    };
                    setItems(prevItems);
                  }}
                  onBlur={(e) => {
                    const formatted = formatNumber(e.target.value);
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      hours: formatted,
                    };
                    setItems(prevItems);
                  }}
                  fullWidth
                  required={item?.required_fields?.hours}
                  disabled={item?.calculate_hours_from_sales || item?.calculate_hours_from_cost}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <div className="flex items-end gap-2">
                  <TextField
                    label="Timepris"
                    variant="standard"
                    type="text"
                    name="hour_rate"
                    value={item?.hour_rate || 0}
                    onChange={(e) => {
                    const rawValue = e.target.value;
                    const cleanedValue = rawValue.replace(",", ".");
                    let prevItems = [...items];
                    
                    // Recalculate hours if auto-calculate is enabled
                    if (item?.calculate_hours_from_sales) {
                      const salesPrice = parseFormattedNumber(item?.sales_price || 0);
                      const quantity = parseFormattedNumber(item?.quantity || 1);
                      const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                      const hourRate = parseFormattedNumber(cleanedValue);
                      if (hourRate > 0 && totalSalesPrice > 0) {
                        const calculatedHours = totalSalesPrice / hourRate;
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                          hours: calculatedHours.toFixed(2),
                          calculated_hours_from_sales: calculatedHours,
                        };
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                        };
                      }
                    } else if (item?.calculate_hours_from_cost) {
                      const costPrice = parseFormattedNumber(item?.purchase_price || 0);
                      const hourRate = parseFormattedNumber(cleanedValue);
                      if (hourRate > 0 && costPrice > 0) {
                        const calculatedHours = costPrice / hourRate;
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                          calculated_hours_from_cost: calculatedHours,
                        };
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                        };
                      }
                    } else {
                      // Always calculate worst case hours for hours lines: (salgspris * mengde) / kostpris timesats
                      if (item?.line_type === "hours" && item?.cost_hour_rate) {
                        const salesPrice = parseFormattedNumber(item?.sales_price || 0);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const costHourRate = parseFormattedNumber(item?.cost_hour_rate || 0);
                        if (costHourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / costHourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            hour_rate: cleanedValue,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            hour_rate: cleanedValue,
                          };
                        }
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                        };
                      }
                    }
                    setItems(prevItems);
                  }}
                  onBlur={(e) => {
                    const formatted = formatNumber(e.target.value);
                    let prevItems = [...items];
                    
                    // Always calculate worst case hours for hours lines when timepris changes: (salgspris * mengde) / kostpris timesats
                    if (item?.line_type === "hours" && item?.cost_hour_rate) {
                      const salesPrice = parseFormattedNumber(item?.sales_price || 0);
                      const quantity = parseFormattedNumber(item?.quantity || 1);
                      const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                      const costHourRate = parseFormattedNumber(item?.cost_hour_rate || 0);
                      if (costHourRate > 0 && totalSalesPrice > 0) {
                        const calculatedHours = totalSalesPrice / costHourRate;
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: formatted,
                          calculated_hours_from_cost: calculatedHours,
                        };
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: formatted,
                        };
                      }
                    } else {
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        hour_rate: formatted,
                      };
                    }
                    setItems(prevItems);
                  }}
                  fullWidth
                  required={item?.required_fields?.hour_rate}
                />
                  {item?.line_type === "hours" && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedItemForHourRate(item);
                        setHourRateSelectorOpen(true);
                      }}
                      title={t("Velg timesats")}
                      className="mb-1"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </IconButton>
                  )}
                </div>
              </div>
              {/* Timekost field - only for hours lines */}
              {item?.line_type === "hours" && (
                <div className="col-span-6 sm:col-span-3">
                  <TextField
                    label="Timekost"
                    variant="standard"
                    type="text"
                    name="cost_hour_rate"
                    value={item?.cost_hour_rate || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      
                      // Recalculate worst case hours when cost hour rate changes: (salgspris * mengde) / kostpris timesats
                      if (item?.sales_price) {
                        const salesPrice = parseFormattedNumber(item?.sales_price || 0);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const costHourRate = parseFormattedNumber(cleanedValue);
                        if (costHourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / costHourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            cost_hour_rate: cleanedValue,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            cost_hour_rate: cleanedValue,
                          };
                        }
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          cost_hour_rate: cleanedValue,
                        };
                      }
                      setItems(prevItems);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      
                      // Recalculate worst case hours when cost hour rate changes: (salgspris * mengde) / kostpris timesats
                      if (item?.sales_price) {
                        const salesPrice = parseFormattedNumber(item?.sales_price || 0);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const costHourRate = parseFormattedNumber(formatted);
                        if (costHourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / costHourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            cost_hour_rate: formatted,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            cost_hour_rate: formatted,
                          };
                        }
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          cost_hour_rate: formatted,
                        };
                      }
                      setItems(prevItems);
                    }}
                    fullWidth
                  />
                </div>
              )}
              <div className="col-span-6 sm:col-span-3">
                <div className="text-right">
                  <div className="text-sm text-gray-500">Timebeløp:</div>
                  <div className="font-semibold">
                    {priceFormatter.format((parseFloat(item?.hours || 0) * parseFloat(item?.hour_rate || 0) * parseFloat(item?.quantity || 1)))}
                  </div>
                </div>
              </div>
            </div>

            {/* Show calculated hours from both sales and cost if available */}
            {(item?.calculated_hours_from_sales || item?.calculated_hours_from_cost || (item?.line_type === "hours" && item?.sales_price && item?.cost_hour_rate)) && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-600 mb-2">Beregnet timeantall:</div>
                <div className="grid grid-cols-2 gap-4">
                  {item?.calculated_hours_from_sales && (
                    <div>
                      <div className="text-xs text-gray-500">Fra salgspris:</div>
                      <div className="font-semibold text-green-700">
                        {item.calculated_hours_from_sales.toFixed(2)}t
                      </div>
                    </div>
                  )}
                  {(item?.calculated_hours_from_cost || (item?.line_type === "hours" && item?.sales_price && item?.cost_hour_rate)) && (
                    <div>
                      <div className="text-xs text-gray-500">Fra kostpris (worst case):</div>
                      <div className="font-semibold text-orange-700">
                        {(item?.calculated_hours_from_cost || (parseFormattedNumber(item?.sales_price || 0) / parseFormattedNumber(item?.cost_hour_rate || 1))).toFixed(2)}t
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Hours Calculation Button - Show if not already visible, but NOT for manual lines or product lines */}
        {!item?.show_hours && item?.line_type !== "manual" && item?.line_type !== "product" && (
          <div className="mt-4">
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                let prevItems = [...items];
                prevItems[item.row_order] = {
                  ...prevItems[item.row_order],
                  show_hours: true,
                };
                setItems(prevItems);
              }}
              startIcon={<PlusIcon className="h-4 w-4" />}
            >
              Legg til timeberegning
            </Button>
          </div>
        )}

        {/* Product Display Options - Show for product lines */}
        {item?.line_type === "product" && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-3">Visningsalternativer:</div>
            <div className="flex items-center gap-4">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item?.show_image !== false}
                    onChange={(e) => {
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        show_image: e.target.checked,
                      };
                      setItems(prevItems);
                    }}
                  />
                }
                label="Vis produktbilde i PDF"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item?.show_url !== false}
                    onChange={(e) => {
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        show_url: e.target.checked,
                      };
                      setItems(prevItems);
                    }}
                  />
                }
                label="Vis produkt-URL i PDF"
              />
            </div>
          </div>
        )}

        {/* Nested Calculations and Products Section - Show for calculation lines and other non-manual lines, but NOT for product lines */}
        {(item?.line_type === "calculation" || (item?.line_type !== "manual" && item?.line_type !== "product")) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-3">Kalkyler og produkter:</div>
            <div className="space-y-2">
            {(() => {
              // Get current item from items state
              const currentItem = items.find(i => i.row_order === item.row_order);
              return currentItem?.nested_calculation && (
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">{currentItem.nested_calculation.title || `Kalkyle #${currentItem.nested_calculation.id}`}</span>
                    <div className="flex items-center gap-2">
                      {props.onEditNestedCalculation && (
                        <PencilSquareIcon
                          className="cursor-pointer h-4 w-4 text-blue-500 hover:text-blue-700"
                          onClick={() => props.onEditNestedCalculation(currentItem.nested_calculation)}
                        />
                      )}
                      <TrashIcon
                        className="cursor-pointer h-4 w-4 text-red-400 hover:text-red-600"
                        onClick={() => handleRemoveNestedCalculation(item, currentItem.nested_calculation.id)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-24">
                        <TextField
                          label="Antall"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={currentItem.quantity || 1}
                          onChange={(e) => {
                            let prevItems = [...items];
                            const itemToUpdate = prevItems.find(i => i.row_order === item.row_order);
                            if (!itemToUpdate) return;
                            prevItems[item.row_order] = {
                              ...itemToUpdate,
                              quantity: parseFloat(e.target.value) || 1,
                            };
                            setItems(prevItems);
                            const mappedItems = prevItems.map(item => ({
                              ...item,
                              item_type: item.item_type || item.type,
                            }));
                            setFieldValue(name, mappedItems);
                          }}
                        />
                      </div>
                      <div className="text-right min-w-[120px]">
                        <div className="text-xs text-gray-500">Total sum:</div>
                        <div className="text-sm font-semibold">
                          {priceFormatter.format(
                            (() => {
                              let nestedAmount = parseFloat(currentItem.nested_calculation.total_amount || 0);
                              const qty = parseFloat(currentItem.quantity || 1);
                              // Apply divide_by_quantity logic if needed
                              if (currentItem.nested_calculation.divide_by_quantity && qty > 0) {
                                nestedAmount = nestedAmount / qty;
                              } else if (currentItem.divide_by_quantity && qty > 0) {
                                nestedAmount = nestedAmount / qty;
                              }
                              return nestedAmount * qty;
                            })()
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state, not from item prop
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              return currentItem?.nested_calculation?.divide_by_quantity || false;
                            })()}
                            onChange={(e) => {
                              let prevItems = [...items];
                              const currentItem = prevItems.find(i => i.row_order === item.row_order);
                              if (!currentItem || !currentItem.nested_calculation) return;
                              
                              const updatedNestedCalc = {
                                ...currentItem.nested_calculation,
                                divide_by_quantity: e.target.checked,
                              };
                              prevItems[item.row_order] = {
                                ...currentItem,
                                nested_calculation: updatedNestedCalc,
                              };
                              setItems(prevItems);
                              const mappedItems = prevItems.map(item => ({
                                ...item,
                                item_type: item.item_type || item.type,
                                // Ensure nested_calculation with divide_by_quantity is included
                                nested_calculation: item.nested_calculation ? {
                                  ...item.nested_calculation,
                                  divide_by_quantity: item.nested_calculation.divide_by_quantity || false,
                                } : null,
                              }));
                              setFieldValue(name, mappedItems);
                            }}
                            size="small"
                          />
                        }
                        label="Del på mengde"
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
            {(() => {
              // Get current item from items state
              const currentItem = items.find(i => i.row_order === item.row_order);
              return currentItem?.products?.map((prod, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">{prod.displayTitle}</span>
                    <TrashIcon
                      className="cursor-pointer h-4 w-4 text-red-400"
                      onClick={() => handleRemoveProduct(item, prod.id)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="grid grid-cols-12 gap-2 flex-1">
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label="Antall"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.quantity || 1}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "quantity", parseFloat(e.target.value) || 1)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label="Salgspris"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.sales_price || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "sales_price", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label="Kostpris"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.purchase_price || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "purchase_price", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label="Rabatt %"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.discount || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "discount", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label="Påslag %"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.surcharge || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "surcharge", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Total:</div>
                          <div className="text-sm font-semibold">
                            {(() => {
                              // Get current values from items state
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              if (!currentProd) return priceFormatter.format(0);
                              
                              const qty = parseFloat(currentProd.quantity || 1);
                              let price = parseFloat(currentProd.sales_price || 0);
                              // Only divide products if product-level divide_by_quantity is set
                              // Item-level divide_by_quantity only affects hours and nested calculations, not products
                              if (currentProd.divide_by_quantity && currentItem.quantity && parseFloat(currentItem.quantity) > 0) {
                                price = price / parseFloat(currentItem.quantity);
                              }
                              const disc = parseFloat(currentProd.discount || 0);
                              const sur = parseFloat(currentProd.surcharge || 0);
                              let total = price * qty;
                              if (sur > 0) total += (total * sur) / 100;
                              if (disc > 0) total -= (total * disc) / 100;
                              return priceFormatter.format(total);
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col gap-2">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state, not from prod prop
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              return currentProd?.divide_by_quantity || false;
                            })()}
                            onChange={(e) => handleProductFieldChange(item, prod.id, "divide_by_quantity", e.target.checked)}
                            size="small"
                          />
                        }
                        label="Del på mengde"
                        className="text-xs"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              return currentProd?.show_image || false;
                            })()}
                            onChange={(e) => handleProductFieldChange(item, prod.id, "show_image", e.target.checked)}
                            size="small"
                          />
                        }
                        label="Vis bilde i PDF"
                        className="text-xs"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              return currentProd?.show_url || false;
                            })()}
                            onChange={(e) => handleProductFieldChange(item, prod.id, "show_url", e.target.checked)}
                            size="small"
                          />
                        }
                        label="Vis URL i PDF"
                        className="text-xs"
                      />
                    </div>
                  </div>
              </div>
            ));
            })()}
            <div className="flex gap-2">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedItemForCalculationSelect(item);
                  setCalculationSelectorOpen(true);
                }}
                startIcon={<PlusIcon className="h-4 w-4" />}
              >
                Legg til kalkyle
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedItemForCalculation(item);
                  handleRowProductSelect(item);
                }}
                startIcon={<PlusIcon className="h-4 w-4" />}
              >
                Legg til produkt
              </Button>
            </div>
          </div>
          </div>
        )}

        {/* Notes Section */}
        {item?.notes && item.notes.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-3">Notater:</div>
            <div className="space-y-2">
              {item.notes.map((note, noteIdx) => (
                <div key={noteIdx} className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {note.title && (
                        <div className="text-sm font-semibold text-gray-900 mb-1">{note.title}</div>
                      )}
                      {note.template_title && (
                        <div className="text-xs text-gray-500 mb-1">Template: {note.template_title}</div>
                      )}
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{note.text || note.description || ""}</div>
                      {note.show_in_offer && (
                        <div className="text-xs text-blue-600 mt-2 font-medium">✓ Vises i tilbudet</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <PencilSquareIcon
                        className="cursor-pointer h-4 w-4 text-blue-400"
                        onClick={() => {
                          setSelectedItemForNote(item);
                          setEditingNoteIndex(noteIdx);
                          setNoteTitle(note.title || "");
                          setNoteText(note.text || note.description || "");
                          setNoteShowInOffer(note.show_in_offer || false);
                          setSelectedTemplate(note.template_id ? { id: note.template_id, title: note.template_title } : null);
                          setNoteDialogOpen(true);
                        }}
                      />
                      <TrashIcon
                        className="cursor-pointer h-4 w-4 text-red-400"
                        onClick={() => {
                          let prevItems = [...items];
                          const updatedNotes = [...(prevItems[item.row_order].notes || [])];
                          updatedNotes.splice(noteIdx, 1);
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            notes: updatedNotes,
                          };
                          setItems(prevItems);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Note Button */}
        <div className="mt-4 flex items-center justify-between">
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setSelectedItemForNote(item);
              setNoteText("");
              setSelectedTemplate(null);
              setNoteDialogOpen(true);
            }}
            startIcon={<PlusIcon className="h-4 w-4" />}
          >
            Legg til notat
          </Button>
          <TrashIcon
            className="cursor-pointer h-5 w-5 text-red-400"
            onClick={() => deleteRow(item.row_order)}
          />
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <React.Fragment>
      {/* Settings Button */}
      <div className="mb-4 flex justify-end">
        <Button
          variant="outlined"
          size="small"
          onClick={() => setSettingsDialogOpen(true)}
        >
          Innstillinger for visning
        </Button>
      </div>

      <div>
        {(() => {
          console.log('[OfferCalculationItems] Rendering items:', {
            items: items,
            itemsLength: items?.length,
            rows: rows,
            rowsLength: rows?.length,
            valuesName: values[name],
            valuesNameLength: values[name]?.length
          });
          
          if (items && items.length > 0) {
            return items.map((item, index) => renderItemRow(item, index));
          } else {
            return (
              <div className="text-gray-500 text-center py-4">
                Ingen linjer. Klikk på &quot;Ny manuell linje&quot; for å legge til en linje.
                <div className="text-xs mt-2 text-gray-400">
                  Debug: items.length={items?.length}, rows.length={rows?.length}, values[{name}].length={values[name]?.length}
                </div>
              </div>
            );
          }
        })()}

        <div className="mt-4 flex justify-between items-center">
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => addItemRow("manual")} variant="contained">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny manuell linje
            </Button>
            <Button onClick={() => addItemRow("calculation")} variant="outlined">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny beregningslinje
            </Button>
            <Button onClick={() => addItemRow("hours")} variant="outlined">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny timelinje
            </Button>
            <Button onClick={() => addItemRow("product")} variant="outlined">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny produktlinje
            </Button>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Total:</div>
            <div className="text-2xl font-bold">
              {priceFormatter.format(calculateTotalPrice(items))}
            </div>
          </div>
        </div>
      </div>

      <ItemSelectorV2
        multiple={selectedItemForCalculation ? true : false}
        open={productSelectorOpen}
        onClose={() => {
          setProductSelectorOpen(false);
          setSelectedItemForCalculation(null);
        }}
        selectedItems={
          selectedItemForCalculation
            ? selectedItemRow?.products || []
            : selectedItemRow?.product
              ? [selectedItemRow.product]
              : []
        }
        onChange={(data) => handleProductSelect(data)}
      />

      <Dialog
        open={calculationSelectorOpen}
        onClose={() => {
          setCalculationSelectorOpen(false);
          setSelectedItemForCalculationSelect(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            height: "80vh",
            maxHeight: "600px",
          },
        }}
      >
        <ModernCalculationSelector
          open={calculationSelectorOpen}
          excludeCalculationId={calculationId}
          selectedCalculations={items
            .filter((item) => item.nested_calculation)
            .map((item) => item.nested_calculation)}
          onChange={(selectedCalculations) => {
            if (selectedItemForCalculationSelect && selectedCalculations && selectedCalculations.length > 0) {
              let prevItems = [...items];
              let item = prevItems[selectedItemForCalculationSelect.row_order];
              item.nested_calculation = {
                ...selectedCalculations[0],
                divide_by_quantity: false,
              };
              item.nested_calculation_id = selectedCalculations[0].id;
              prevItems[selectedItemForCalculationSelect.row_order] = item;
              setItems(prevItems);
              setCalculationSelectorOpen(false);
              setSelectedItemForCalculationSelect(null);
            }
          }}
          onClose={() => {
            setCalculationSelectorOpen(false);
            setSelectedItemForCalculationSelect(null);
          }}
          multiple={false}
          showHeader={true}
        />
      </Dialog>

      {/* Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => {
          setNoteDialogOpen(false);
          setSelectedItemForNote(null);
          setEditingNoteIndex(null);
          setNoteTitle("");
          setNoteText("");
          setNoteShowInOffer(false);
          setSelectedTemplate(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingNoteIndex !== null ? "Rediger notat" : "Legg til notat"}</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <TextField
              label="Tittel"
              variant="standard"
              fullWidth
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              placeholder="Tittel på notatet..."
            />
            <Button
              variant="outlined"
              onClick={() => setTemplateSelectorOpen(true)}
              fullWidth
            >
              {selectedTemplate ? `Template: ${selectedTemplate.title}` : "Velg template (valgfritt)"}
            </Button>
            {selectedTemplate && (
              <div className="p-3 bg-gray-50 rounded border border-gray-200">
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTemplate.content}</div>
              </div>
            )}
            <TextField
              label="Beskrivelse"
              variant="standard"
              fullWidth
              multiline
              rows={6}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Skriv inn beskrivelse eller velg en template..."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={noteShowInOffer}
                  onChange={(e) => setNoteShowInOffer(e.target.checked)}
                />
              }
              label="Vis i tilbudet"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNoteDialogOpen(false);
            setSelectedItemForNote(null);
            setEditingNoteIndex(null);
            setNoteTitle("");
            setNoteText("");
            setNoteShowInOffer(false);
            setSelectedTemplate(null);
          }}>
            Avbryt
          </Button>
          <Button
            onClick={() => {
              if (selectedItemForNote && (noteText || selectedTemplate || noteTitle)) {
                let prevItems = [...items];
                const currentNotes = [...(prevItems[selectedItemForNote.row_order].notes || [])];
                const noteData = {
                  title: noteTitle || null,
                  text: noteText || selectedTemplate?.content || "",
                  description: noteText || selectedTemplate?.content || "",
                  template_id: selectedTemplate?.id || null,
                  template_title: selectedTemplate?.title || null,
                  show_in_offer: noteShowInOffer,
                };
                
                if (editingNoteIndex !== null) {
                  // Update existing note
                  currentNotes[editingNoteIndex] = noteData;
                } else {
                  // Add new note
                  currentNotes.push(noteData);
                }
                
                prevItems[selectedItemForNote.row_order] = {
                  ...prevItems[selectedItemForNote.row_order],
                  notes: currentNotes,
                };
                setItems(prevItems);
                setNoteDialogOpen(false);
                setSelectedItemForNote(null);
                setEditingNoteIndex(null);
                setNoteTitle("");
                setNoteText("");
                setNoteShowInOffer(false);
                setSelectedTemplate(null);
              }
            }}
            variant="contained"
            disabled={!noteText && !selectedTemplate && !noteTitle}
          >
            {editingNoteIndex !== null ? "Oppdater" : "Lagre"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Selector Dialog */}
      <Dialog
        open={templateSelectorOpen}
        onClose={() => setTemplateSelectorOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Velg template</DialogTitle>
        <DialogContent>
          <div className="space-y-2 mt-2">
            {Array.isArray(templates) && templates.length > 0 ? (
              templates.map((template) => (
                <div
                  key={template.id}
                  className="p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setNoteText(template.content || "");
                    setTemplateSelectorOpen(false);
                  }}
                >
                  <div className="font-medium">{template.title}</div>
                  {template.content && (
                    <div className="text-sm text-gray-500 mt-1 line-clamp-2">{template.content}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">Ingen templates funnet</div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateSelectorOpen(false)}>Avbryt</Button>
        </DialogActions>
      </Dialog>

      {/* Hour Rate Selector Dialog */}
      <Dialog
        open={hourRateSelectorOpen}
        onClose={() => {
          setHourRateSelectorOpen(false);
          setSelectedItemForHourRate(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Velg timesats</DialogTitle>
        <DialogContent>
          <div className="space-y-2 mt-2">
            {Array.isArray(hourRates) && hourRates.length > 0 ? (
              hourRates.map((hourRate) => (
                <div
                  key={hourRate.id}
                  className="p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    if (selectedItemForHourRate) {
                      let prevItems = [...items];
                      const hourRateValue = hourRate.hour_rate || 0;
                      const costRateValue = hourRate.cost_rate || 0;
                      
                      prevItems[selectedItemForHourRate.row_order] = {
                        ...prevItems[selectedItemForHourRate.row_order],
                        hour_rate: hourRateValue.toString(),
                        // Don't set purchase_price from cost_rate - that's for the line's cost price
                        hour_rate_id: hourRate.id, // Store reference to hour rate
                        cost_hour_rate: costRateValue.toString(), // Store cost hour rate for worst case calculation
                      };
                      
                      // Recalculate hours if auto-calculate is enabled
                      if (selectedItemForHourRate?.calculate_hours_from_sales) {
                        const salesPrice = parseFormattedNumber(selectedItemForHourRate.sales_price || 0);
                        const quantity = parseFormattedNumber(selectedItemForHourRate.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        if (hourRateValue > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / hourRateValue;
                          prevItems[selectedItemForHourRate.row_order].hours = calculatedHours.toFixed(2);
                          prevItems[selectedItemForHourRate.row_order].calculated_hours_from_sales = calculatedHours;
                        }
                      }
                      
                      // Always calculate worst case hours for hours lines: (salgspris * mengde) / kostpris timesats
                      if (selectedItemForHourRate?.line_type === "hours" && costRateValue > 0) {
                        const salesPrice = parseFormattedNumber(selectedItemForHourRate.sales_price || 0);
                        const quantity = parseFormattedNumber(selectedItemForHourRate.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        if (totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / costRateValue;
                          prevItems[selectedItemForHourRate.row_order].calculated_hours_from_cost = calculatedHours;
                        }
                      }
                      
                      setItems(prevItems);
                    }
                    setHourRateSelectorOpen(false);
                    setSelectedItemForHourRate(null);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{hourRate.title}</div>
                      {hourRate.description && (
                        <div className="text-sm text-gray-500 mt-1 line-clamp-2">{hourRate.description}</div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-semibold text-gray-900">
                        {priceFormatter.format(hourRate.hour_rate || 0)}
                      </div>
                      {hourRate.cost_rate > 0 && (
                        <div className="text-xs text-gray-500">
                          Kost: {priceFormatter.format(hourRate.cost_rate)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                Ingen timesatser funnet. 
                <br />
                <a 
                  href="/dashboard/offers/hour-rates/new" 
                  className="text-indigo-600 hover:text-indigo-800 underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open("/dashboard/offers/hour-rates/new", "_blank");
                  }}
                >
                  Opprett ny timesats
                </a>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setHourRateSelectorOpen(false);
            setSelectedItemForHourRate(null);
          }}>
            Avbryt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Innstillinger for visning i tilbud/PDF</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <div className="text-sm text-gray-600 mb-4">
              Disse innstillingene styrer hva som vises i PDF og tilbudet for kunden. De påvirker ikke redigeringsvisningen.
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHours}
                  onChange={(e) => setShowHours(e.target.checked)}
                />
              }
              label="Vis timeantall i tilbud/PDF"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showQuantities}
                  onChange={(e) => setShowQuantities(e.target.checked)}
                />
              }
              label="Vis mengder i tilbud/PDF"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showProducts}
                  onChange={(e) => setShowProducts(e.target.checked)}
                />
              }
              label="Vis produkter i tilbud/PDF"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSetupOnly}
                  onChange={(e) => setShowSetupOnly(e.target.checked)}
                />
              }
              label="Hvis oppsett kalkyle"
            />
            <div className="text-sm text-gray-500 mt-1">
              Når aktivert, vises ikke produkter, beregninger eller timeberegning i PDF. Kun tittel, beskrivelse og delbeløp vises.
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showQuantitiesForOfferItemOnly}
                  onChange={(e) => setShowQuantitiesForOfferItemOnly(e.target.checked)}
                />
              }
              label="Hvis mengder tilbudspost"
            />
            <div className="text-sm text-gray-500 mt-1">
              Når aktivert, vises ikke antall for produkter/beregninger knyttet til kalkylen. Kun mengde for tilbudspost/kalkyle vises.
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeProductShortDescription}
                  onChange={(e) => setIncludeProductShortDescription(e.target.checked)}
                />
              }
              label="Medta kort beskrivelse produkt"
            />
            <div className="text-sm text-gray-500 mt-1">
              Når aktivert, vises kort beskrivelse (short_description) fra produktet i PDF når produkter vises med bilde eller URL.
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>
            Lukk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calculation Settings Dialog */}
      <Dialog
        open={calculationSettingsDialogOpen}
        onClose={() => {
          setCalculationSettingsDialogOpen(false);
          setSelectedItemForSettings(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Innstillinger for beregningslinje</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={(() => {
                    // Get current value from items state, not from selectedItemForSettings
                    if (selectedItemForSettings) {
                      const currentItem = items.find(i => i.row_order === selectedItemForSettings.row_order);
                      return currentItem?.divide_by_quantity || false;
                    }
                    return false;
                  })()}
                  onChange={(e) => {
                    if (selectedItemForSettings) {
                      let prevItems = [...items];
                      const updatedItem = {
                        ...prevItems[selectedItemForSettings.row_order],
                        divide_by_quantity: e.target.checked,
                      };
                      prevItems[selectedItemForSettings.row_order] = updatedItem;
                      setItems(prevItems);
                      // Update selectedItemForSettings to reflect the change
                      setSelectedItemForSettings(updatedItem);
                      // Update Formik form
                      const mappedItems = prevItems.map(item => ({
                        ...item,
                        item_type: item.item_type || item.type,
                      }));
                      setFieldValue(name, mappedItems);
                    }
                  }}
                />
              }
              label="Del beregning på mengde"
            />
            <div className="text-sm text-gray-500 mt-2">
              Når aktivert, vil kalkyle-beløpet og timer deles på mengden i linjen. 
              For eksempel: Hvis mengde er 8, vil kalkyle-beløpet og timer deles på 8.
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCalculationSettingsDialogOpen(false);
            setSelectedItemForSettings(null);
          }}>
            Lukk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Field Dialog */}
      <Dialog
        open={customFieldDialogOpen}
        onClose={() => {
          setCustomFieldDialogOpen(false);
          setEditingCustomField(null);
          setEditingItemForCustomField(null);
          setNewCustomField({ key: "", label: "", type: "text", value: "" });
        }}
      >
        <DialogTitle>
          {editingCustomField ? "Rediger custom field" : "Legg til custom field"}
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <TextField
              label="Nøkkel (key)"
              variant="standard"
              fullWidth
              value={newCustomField.key}
              onChange={(e) =>
                setNewCustomField({ ...newCustomField, key: e.target.value })
              }
              disabled={!!editingCustomField}
            />
            <TextField
              label="Label"
              variant="standard"
              fullWidth
              value={newCustomField.label}
              onChange={(e) =>
                setNewCustomField({ ...newCustomField, label: e.target.value })
              }
            />
            <FormControl variant="standard" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={newCustomField.type}
                onChange={(e) =>
                  setNewCustomField({ ...newCustomField, type: e.target.value })
                }
                label="Type"
              >
                <MenuItem value="text">Tekst</MenuItem>
                <MenuItem value="number">Nummer</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Verdi"
              variant="standard"
              fullWidth
              type={newCustomField.type === "number" ? "number" : "text"}
              value={newCustomField.value}
              onChange={(e) =>
                setNewCustomField({ ...newCustomField, value: e.target.value })
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCustomFieldDialogOpen(false);
              setEditingCustomField(null);
              setEditingItemForCustomField(null);
              setNewCustomField({ key: "", label: "", type: "text", value: "" });
            }}
          >
            Avbryt
          </Button>
          <Button onClick={handleSaveCustomField} variant="contained">
            Lagre
          </Button>
        </DialogActions>
      </Dialog>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Er du sikker på at du vil slette denne raden?"}
        action="delete"
        module="offer_calculation_item"
        yesCallback={() => handleDeleteRow()}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </React.Fragment>
  );
}

